import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCreateDraft, type CompileResult } from "@/monitors/api";

interface Props {
  initialStatement?: string;
  onCompiled: (result: CompileResult) => void;
}

export function Step1Statement({ initialStatement = "", onCompiled }: Props) {
  const [statement, setStatement] = useState(initialStatement);
  const mutation = useCreateDraft();

  const onSubmit = async () => {
    const res = await mutation.mutateAsync({ statement });
    onCompiled(res);
  };

  const canSubmit = statement.trim().length >= 4 && !mutation.isPending;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
      <div>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Natural-language statement
        </label>
        <Textarea
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          placeholder="e.g. if trump announces a gold trade deal with china during his visit to china"
          rows={5}
          className="mt-2"
        />
        {mutation.error && (
          <p className="text-sm text-red-600 mt-2">
            {mutation.error.message ?? "Couldn't compile. Try again."}
          </p>
        )}
        <Button onClick={onSubmit} disabled={!canSubmit} className="mt-4">
          {mutation.isPending ? "Compiling…" : "Compile →"}
        </Button>
      </div>
      <Card>
        <CardContent className="text-sm space-y-2 p-4">
          <p className="font-semibold">Tips</p>
          <p>• Name actors specifically (Trump, ECB, China…).</p>
          <p>• Add "rules out", "cancels" to <em>excludes</em> if you only want positive events.</p>
          <p>• Polarity matters: "deal collapses" ≠ "deal signed".</p>
        </CardContent>
      </Card>
    </div>
  );
}
